import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { Award, Users, Car, Shield, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi from "../services/odooApi";

const ICON_MAP = {
  shield: Shield, clock: Award, star: Award, award: Award, users: Users, car: Car, 'map-pin': Shield,
};

export const AboutPage = () => {
  const { t } = useLanguage();
  const [pageContent, setPageContent] = useState(null);
  const [stats, setStats] = useState([]);
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [page, s, f] = await Promise.all([
        odooApi.getPageContent('about'),
        odooApi.getStats(),
        odooApi.getFeatures(),
      ]);
      if (page) setPageContent(page);
      if (s?.length) setStats(s);
      if (f?.length) setFeatures(f);
    };
    load();
  }, []);

  const displayStats = stats.length > 0 ? stats : [
    { label: t("about.stat.yearsExperience", "Years Experience"), value: "10+" },
    { label: t("home.statsHappyCustomers", "Happy Customers"), value: "50,000+" },
    { label: t("about.stat.premiumVehicles", "Premium Vehicles"), value: "1,200+" },
    { label: t("home.statsPartnerSuppliers", "Partner Suppliers"), value: "150+" },
  ];

  const displayFeatures = features.length > 0 ? features : [
    { icon: 'award', title: t("about.feature.excellence.title", "Excellence"), description: t("about.feature.excellence.description", "We strive for excellence in every aspect of our service.") },
    { icon: 'users', title: t("about.feature.customerFirst.title", "Customer First"), description: t("about.feature.customerFirst.description", "Your satisfaction is our priority.") },
    { icon: 'car', title: t("about.feature.premiumFleet.title", "Premium Fleet"), description: t("about.feature.premiumFleet.description", "Only the finest vehicles make it to our collection.") },
    { icon: 'shield', title: t("about.feature.trustSafety.title", "Trust & Safety"), description: t("about.feature.trustSafety.description", "Comprehensive insurance and 24/7 support.") },
  ];

  return (
    <>
      <SEO
        title={t("about.metaTitle", "About Us | UAE's Premier Luxury Car Rental Service")}
        description="Learn about Velaris Royal Car Rental - UAE's premier luxury car rental service with 10+ years of experience. Serving Dubai, Abu Dhabi, Sharjah and all emirates."
        keywords="about velaris royal, luxury car rental company dubai, car rental service uae, premium car hire dubai"
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("about.title", "About Us") },
        ]}
      />

      <div className="min-h-screen bg-white" data-testid="about-page">
        {/* Hero */}
        <div className="relative h-[50vh] min-h-[400px] bg-black overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1920&h=800&fit=crop"
            alt={t("about.heroAlt", "Luxury cars")}
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <span className="inline-block bg-gold-gradient text-black px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold mb-6 w-fit">
              {t("about.storyEyebrow", "Our Story")}
            </span>
            <h1 className="font-[var(--font-display)] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 max-w-3xl">
              {pageContent?.title || t("about.storyTitle", "Redefining Luxury Car Rental in UAE")}
            </h1>
            <p className="text-white/70 text-lg max-w-xl">
              {pageContent?.subtitle || t("about.storySubtitle", "Since 2014, delivering exceptional car rental experiences.")}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-black border-t border-[#D4AF37]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {displayStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl sm:text-4xl font-bold text-gold-gradient">{stat.value}</p>
                  <p className="text-white/60 mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Story Section */}
        <section className="py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold">
                  {t("about.whoWeAreEyebrow", "Who We Are")}
                </span>
                <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-6">
                  {t("about.legacyTitle", "A Legacy of Excellence")}
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  {pageContent?.content ? (
                    <div dangerouslySetInnerHTML={{ __html: pageContent.content }} />
                  ) : (
                    <>
                      {t(
                        "about.legacyContent",
                        "Velaris Royal Car Rental was founded with a vision to transform the car rental experience in the UAE. What started as a small fleet of luxury vehicles has grown into one of the region's most trusted names in premium car hire.\n\nOur commitment to excellence extends beyond just providing vehicles. We offer a complete luxury experience from the moment you browse our collection to when you return the keys.\n\nToday, we partner with over 150 premium suppliers, offering access to more than 1,200 vehicles across all emirates.",
                      )
                        .split("\n\n")
                        .map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                    </>
                  )}
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&h=800&fit=crop"
                  alt={t("about.storyImageAlt", "Luxury car")}
                  className="rounded-lg shadow-2xl"
                />
                <div className="absolute -bottom-8 -left-8 bg-gold-gradient p-8 rounded-lg">
                  <p className="text-4xl font-bold text-black">10+</p>
                  <p className="text-black/70">{t("about.yearsOfExcellence", "Years of Excellence")}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 md:py-32 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold">
                {t("about.valuesEyebrow", "Our Values")}
              </span>
              <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl font-bold text-gray-900 mt-4">
                {t("about.valuesTitle", "What Drives Us")}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {displayFeatures.map((feature, index) => {
                const Icon = ICON_MAP[feature.icon] || Shield;
                return (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-lg border-l-4 border-transparent hover:border-[#D4AF37] hover:shadow-lg transition-all"
                  >
                    <div className="w-14 h-14 bg-gold-gradient rounded-lg flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-black" />
                    </div>
                    <h3 className="font-[var(--font-display)] text-xl font-semibold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-32 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl font-bold text-white mb-6">
              {t("about.ctaTitle", "Ready to Experience Royal Service?")}
            </h2>
            <p className="text-white/70 text-lg mb-10">
              {t("cta.subtitle", "Browse our premium collection and find your perfect ride today.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cars"
                className="btn-gold px-10 py-4 text-black font-bold uppercase tracking-wider rounded-sm inline-flex items-center justify-center"
              >
                {t("btn.browse", "Browse Cars")}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Link>
              <Link
                to="/contact"
                className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] px-10 py-4 font-bold uppercase tracking-wider rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors inline-flex items-center justify-center"
              >
                {t("btn.contactUs", "Contact Us")}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;

